import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { usePaneStore } from "../state/store-context";
import type { LineItemRow, LineItemFieldState } from "../state/types";
import styles from "./LineItemGrid.module.css";

export interface LineItemColumn {
  name: string;
  label: string;
  dataType: string; // text, number, currency, date
  width?: string;
  isCalculated?: boolean;
  formula?: string; // e.g. "qty * unit_price"
}

interface LineItemGridProps {
  groupId: string;
  groupName: string;
  columns: LineItemColumn[];
  rows: LineItemRow[];
}

/**
 * Evaluate a simple arithmetic formula like "qty * unit_price"
 * against row field values. Only supports +, -, *, / and field references.
 */
function evaluateFormula(formula: string, rowFields: Record<string, LineItemFieldState>): string {
  try {
    // Replace field names with their numeric values
    let expr = formula;
    for (const [name, field] of Object.entries(rowFields)) {
      const num = parseFloat(field.value) || 0;
      // Replace whole-word occurrences of the field name
      expr = expr.replace(new RegExp(`\\b${escapeRegex(name)}\\b`, "g"), String(num));
    }
    // Only allow numbers, whitespace, and basic operators
    if (!/^[\d\s+\-*/.()]+$/.test(expr)) return "";
    // eslint-disable-next-line no-new-func
    const result = new Function(`return (${expr})`)() as number;
    return isFinite(result) ? result.toFixed(2) : "";
  } catch {
    return "";
  }
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function formatCurrency(val: string): string {
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  return `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function confidenceClass(confidence: number | null): string {
  if (confidence === null) return "";
  if (confidence >= 0.85) return styles.confidenceHigh ?? "";
  if (confidence >= 0.7) return styles.confidenceMedium ?? "";
  return styles.confidenceLow ?? "";
}

function validationDot(status: string): string {
  if (status === "pass") return styles.dotPass ?? "";
  if (status === "fail") return styles.dotFail ?? "";
  if (status === "warn") return styles.dotWarn ?? "";
  return styles.dotPending ?? "";
}

/** P2b — the outline class for a cell flagged by a row check. */
function cellStatusClass(status: string): string {
  if (status === "fail") return styles.cellFail ?? "";
  if (status === "warn") return styles.cellWarn ?? "";
  return "";
}

export function LineItemGrid({ groupId, groupName, columns, rows }: LineItemGridProps) {
  const addLineItemRow = usePaneStore((s) => s.addLineItemRow);
  const removeLineItemRow = usePaneStore((s) => s.removeLineItemRow);
  const updateLineItemCell = usePaneStore((s) => s.updateLineItemCell);
  // P2b — per-row check outcomes for THIS group (keyed by definition id).
  const rowCheckResults = usePaneStore((s) => s.rowCheckResults[groupId]);
  const focusRowRequest = usePaneStore((s) => s.focusRowRequest);

  // Failing entries per row number → { severity, messages } for the row badge.
  const failuresByRow = useMemo(() => {
    const map = new Map<number, { severity: "error" | "warn"; messages: string[] }>();
    for (const e of rowCheckResults ?? []) {
      if (e.passed) continue;
      const cur = map.get(e.rowNumber) ?? { severity: "warn" as "error" | "warn", messages: [] };
      if (e.severity === "error") cur.severity = "error";
      if (e.message) cur.messages.push(e.message);
      map.set(e.rowNumber, cur);
    }
    return map;
  }, [rowCheckResults]);

  // Active cell: [rowIndex, colIndex]
  const [activeCell, setActiveCell] = useState<[number, number]>([0, 0]);
  const gridRef = useRef<HTMLTableElement>(null);
  const cellRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const cellKey = (row: number, col: number) => `${row}-${col}`;

  // Focus active cell
  useEffect(() => {
    const key = cellKey(activeCell[0], activeCell[1]);
    const input = cellRefs.current.get(key);
    if (input) {
      input.focus();
      input.select();
    }
  }, [activeCell]);

  // P2b — a doc-panel chip click asks us to focus the first failing row of this
  // group; jump the active cell to its highlightColumn (or column 0).
  useEffect(() => {
    if (!focusRowRequest || focusRowRequest.groupId !== groupId) return;
    const rowIndex = rows.findIndex((r) => r.rowNumber === focusRowRequest.rowNumber);
    if (rowIndex < 0) return;
    setActiveCell([rowIndex, 0]);
  }, [focusRowRequest, groupId, rows]);

  const navigateCell = useCallback(
    (rowDir: number, colDir: number) => {
      setActiveCell(([r, c]) => {
        let nextCol = c + colDir;
        let nextRow = r + rowDir;

        // Horizontal wrap
        if (nextCol >= columns.length) {
          nextCol = 0;
          nextRow += 1;
        } else if (nextCol < 0) {
          nextCol = columns.length - 1;
          nextRow -= 1;
        }

        // Skip calculated columns
        while (
          nextRow >= 0 &&
          nextRow < rows.length &&
          nextCol >= 0 &&
          nextCol < columns.length &&
          columns[nextCol]?.isCalculated
        ) {
          nextCol += colDir || 1;
          if (nextCol >= columns.length) {
            nextCol = 0;
            nextRow += 1;
          } else if (nextCol < 0) {
            nextCol = columns.length - 1;
            nextRow -= 1;
          }
        }

        // Clamp
        if (nextRow < 0) nextRow = 0;
        if (nextRow >= rows.length) nextRow = rows.length - 1;
        if (nextCol < 0) nextCol = 0;
        if (nextCol >= columns.length) nextCol = columns.length - 1;

        return [nextRow, nextCol];
      });
    },
    [columns, rows.length],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Tab" || e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) {
          navigateCell(0, -1);
        } else {
          navigateCell(0, 1);
        }
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        navigateCell(-1, 0);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        navigateCell(1, 0);
        return;
      }
      if (e.key === "n" && e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();
        addLineItemRow(groupId);
        // Move to new row after render
        setTimeout(() => setActiveCell([rows.length, 0]), 0);
        return;
      }
      if (e.key === "d" && e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();
        if (rows.length > 0) {
          const [rowIdx] = activeCell;
          const row = rows[rowIdx];
          const hasData = row && Object.values(row.fields).some((f) => f.value !== "");
          if (!hasData || window.confirm("Delete this row?")) {
            removeLineItemRow(groupId, rowIdx);
            setActiveCell(([r, c]) => [Math.max(0, Math.min(r, rows.length - 2)), c]);
          }
        }
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        // Blur out of grid — return focus to the field panel
        (document.activeElement as HTMLElement)?.blur();
        return;
      }
    },
    [navigateCell, addLineItemRow, removeLineItemRow, groupId, rows, activeCell],
  );

  const handleCellChange = useCallback(
    (rowIndex: number, colName: string, value: string) => {
      updateLineItemCell(groupId, rowIndex, colName, value);
    },
    [groupId, updateLineItemCell],
  );

  // Column totals for numeric columns
  const totals = useMemo(() => {
    const result: Record<string, number> = {};
    for (const col of columns) {
      if (col.dataType === "number" || col.dataType === "currency") {
        let sum = 0;
        for (const row of rows) {
          if (col.isCalculated && col.formula) {
            sum += parseFloat(evaluateFormula(col.formula, row.fields)) || 0;
          } else {
            sum += parseFloat(row.fields[col.name]?.value ?? "0") || 0;
          }
        }
        result[col.name] = sum;
      }
    }
    return result;
  }, [columns, rows]);

  return (
    <div className={styles.container} data-testid="line-item-grid">
      <div className={styles.header}>
        <span className={styles.title}>{groupName}</span>
        <button
          className={styles.addBtn}
          onClick={() => addLineItemRow(groupId)}
          title="Add row (Ctrl+N)"
        >
          + Add Row
        </button>
      </div>
      <table
        ref={gridRef}
        className={styles.table}
        onKeyDown={handleKeyDown}
        role="grid"
      >
        <thead>
          <tr>
            <th className={styles.rowNumHeader}>#</th>
            {columns.map((col) => (
              <th
                key={col.name}
                className={styles.colHeader}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={row.id}
              className={`${styles.row} ${activeCell[0] === rowIndex ? styles.activeRow : ""}`}
            >
              <td className={styles.rowNum}>
                {row.rowNumber}
                {(() => {
                  const fail = failuresByRow.get(row.rowNumber);
                  if (!fail) return null;
                  return (
                    <span
                      data-testid={`row-badge-${row.rowNumber}`}
                      className={`${styles.rowBadge} ${fail.severity === "error" ? styles.rowBadgeFail : styles.rowBadgeWarn}`}
                      title={fail.messages.join("; ") || (fail.severity === "error" ? "Row check failed" : "Row check warning")}
                      aria-label={`Row ${row.rowNumber} ${fail.severity === "error" ? "failed" : "warning"}: ${fail.messages.join("; ")}`}
                    />
                  );
                })()}
              </td>
              {columns.map((col, colIndex) => {
                const fieldState = row.fields[col.name];
                const isActive =
                  activeCell[0] === rowIndex && activeCell[1] === colIndex;

                if (col.isCalculated && col.formula) {
                  const computed = evaluateFormula(col.formula, row.fields);
                  const display =
                    col.dataType === "currency" ? formatCurrency(computed) : computed;
                  return (
                    <td
                      key={col.name}
                      className={`${styles.cell} ${styles.calculated}`}
                      data-testid={`cell-${rowIndex}-${col.name}`}
                    >
                      {display}
                    </td>
                  );
                }

                const value = fieldState?.value ?? "";
                const confidence = fieldState?.ocrConfidence ?? null;
                const valStatus = fieldState?.validationStatus ?? "pending";

                return (
                  <td
                    key={col.name}
                    className={`${styles.cell} ${isActive ? styles.activeCell : ""} ${cellStatusClass(valStatus)}`}
                  >
                    <div className={styles.cellInner}>
                      <span className={`${styles.dot} ${validationDot(valStatus)}`} />
                      <input
                        ref={(el) => {
                          if (el) {
                            cellRefs.current.set(cellKey(rowIndex, colIndex), el);
                          }
                        }}
                        type="text"
                        className={`${styles.cellInput} ${confidenceClass(confidence)}`}
                        value={value}
                        onChange={(e) =>
                          handleCellChange(rowIndex, col.name, e.target.value)
                        }
                        onFocus={() => setActiveCell([rowIndex, colIndex])}
                        data-testid={`cell-input-${rowIndex}-${col.name}`}
                        aria-label={`${col.label} row ${rowIndex + 1}`}
                      />
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + 1}
                className={styles.emptyRow}
              >
                No line items. Press Ctrl+N or click &quot;+ Add Row&quot;.
              </td>
            </tr>
          )}
        </tbody>
        {Object.keys(totals).length > 0 && rows.length > 0 && (
          <tfoot>
            <tr className={styles.totalsRow}>
              <td className={styles.rowNum} />
              {columns.map((col) => {
                const total = totals[col.name];
                return (
                  <td key={col.name} className={styles.totalCell}>
                    {total !== undefined
                      ? col.dataType === "currency"
                        ? formatCurrency(total.toFixed(2))
                        : total.toFixed(2)
                      : ""}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
